import "../SpaDetailsPage.css"
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {SpaLiveLabels} from "../../../mqttData/live/spaLiveLabels.enums";
import { SpaSettingsLabels } from "../../../mqttData/settings/spaSettingsLabels";
import Switch from '@mui/material/Switch';
import { SpaConstLabels } from "../../../mqttData/consts/spaConstsLabels.enums";
import {useNavigate} from "react-router-dom";
import { resetErrorData } from "../../../redux/reducers/error.reducer";
import { resetLiveData } from "../../../redux/reducers/live.reducer";
import { resetSettingsData } from "../../../redux/reducers/settings.reducer";
import { resetUpdateStatusData } from "../../../redux/reducers/updateStatus.reducer";
import { resetConstData } from "../../../redux/reducers/const.reducer";
import { useParams } from "react-router-dom";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { ActiveCycleStyleMap } from "../../../mqttData/live/heatCycle.enums";
import { upperCase } from "lodash";
import { filterStateLabels } from "../../../mqttData/live/filterState";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { SmartPHState, SmartPHStateTextMap } from "../../../mqttData/live/smartPhState.enums";
import { palName } from "../../../utils/brand";
import { ChevronRightIcon } from "../../../components/common/Icons/Icons";
import { hasData } from "./SpecGroup";
import { isV1Spa } from "../../../hooks/useSpaCommand";

const DetailsGrid: React.FC<{editMode:any, newSettings:any, setNewSettings:any, handleBoost:any}> = ({editMode, newSettings, setNewSettings, handleBoost}) => {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const spa = useSelector((state: RootState) => state.spa.data);
  const isV1 = isV1Spa(spa);
  const show = (...values: unknown[]) => !isV1 || values.some(hasData);
  // Get the current connection status
  const isConnected = useSelector((state: RootState) => 
    state.spa?.currentSpaId === id && state.spa?.isCurrentSpaConnected
  );

  // Spa data and settings:
  const liveData = useSelector((state: RootState) => state.live.data);
  const settingsData = useSelector((state: RootState) => state.settings.data);
  const constData = useSelector((state: RootState) => state.const.data);
  const statusData = useSelector((state: RootState) => state.status.data);
  const {canAccess:canEnableManualMode} = usePermissionCheck(['can_enable_manual_mode']);
  // Purge time is set by role, not by the dev-controls permission: admins
  // need it and do not hold that permission.
  const {canAccess:canSetPurgeTime} = useRoleCheck(['admin', 'developer']);

  // Local state for PT switch - initialize based on settingsData.PT
  const [isPTEnabled, setIsPTEnabled] = useState(settingsData.PT !== 96);
  // Store the last selected time value (for when switch is toggled back on)
  const [lastSelectedPT, setLastSelectedPT] = useState(settingsData.PT !== 96 ? settingsData.PT : 0);
  // Current PT value for the time picker (to handle immediate updates)
  const [currentPT, setCurrentPT] = useState(settingsData.PT);

  // Update local state when settingsData.PT changes
  useEffect(() => {
    setIsPTEnabled(settingsData.PT !== 96);
    setCurrentPT(settingsData.PT);
    // Only update lastSelectedPT if it's a valid time (not OFF)
    if (settingsData.PT !== 96) {
      setLastSelectedPT(settingsData.PT);
    }
  }, [settingsData.PT]);

  useEffect(() => {
    if (liveData && liveData?.spa_guid && liveData.spa_guid!==id) {
        dispatch(resetLiveData());
        dispatch(resetSettingsData());
        dispatch(resetUpdateStatusData());
        dispatch(resetErrorData());
        dispatch(resetConstData());
    }
}, [dispatch, id, liveData]);
  
  let valueMapper: { [p: number]: string };

  valueMapper = {
    0:'OFF',
    1:'P1low',
    2:'P1high',
    3:'Yess',   
    15 : 'LOW',
    100 : 'HIGH',
  }

  let orpLevelMapper: { [p: number]: [number, number] };
  let orpLevelMapperReverse: { [p: number]: number };

  orpLevelMapper = {
    0: [545, 555],
    50: [645, 655],
    100: [745, 755],
  };

  orpLevelMapperReverse = {
    555: 0,
    655: 50,
    755: 100,
  };


    const getConvertedValue = (value:any, convert = 100) => {
        return value ? (value as number) / convert : 0;
    };

    const getConfigDisplay = (value:any, type:string) => {
      switch(type){
        case `config`:
          return value ? <span className="check">✓</span> :<span className="ex">X</span>;

        case `isOn`:
          return value ? 'ON' :'OFF';;

        case `isConnected`:
          return value ? 'CONNECTED' :'DISCONNECTED';

        case `purgeTime` :
          return (value!==null && value!==undefined) ? value === 96 ? `OFF` : value : `N/A`
      }
    }

    // Helper functions for PT time conversion
    const ptToHoursMinutes = (ptValue: number): { hours: number, minutes: number } => {
      // PT = 96 means OFF, so we shouldn't convert it to time
      // This function should only be called when PT is not 96
      const totalMinutes = ptValue * 15; // Each PT unit = 15 minutes
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return { hours, minutes };
    };

    const hoursMinutesToPt = (hours: number, minutes: number): number => {
      const totalMinutes = hours * 60 + minutes;
      return Math.round(totalMinutes / 15); // Convert back to PT units (15-minute intervals)
    };

    // Helper function to format PT display as "Time (PT)"
    const formatPTDisplay = (ptValue: number): string => {
      if (ptValue === 96) {
        return 'OFF';
      }
      const { hours, minutes } = ptToHoursMinutes(ptValue);
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      return `${timeString} (${ptValue})`;
    };

  return (
    <>
      <div className="details-grid">
        <table className="details-table" cellPadding={0} cellSpacing={0}>
        <thead>
          <tr>
            <th className="labelled-cell">
              <span>Pack Current: </span>
              <span>{getConvertedValue(liveData[SpaLiveLabels.Current])}A</span>
            </th>
            <th>Config</th>
            <th colSpan={5}>Live</th>
            <th colSpan={3}>Settings</th>
          </tr>
        </thead>

        <tbody>
          {/* HEAT */}
          <tr>
            <td className="title"></td>
            <td className="config">Heat</td>

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.H1] ?"active":""}>{upperCase(ActiveCycleStyleMap[(liveData[SpaLiveLabels.H1] as number) ?? 0])}</td>
            <td>
              <div className="labelled-cell">
                <span>Spa: </span>
                <span className="active">{liveData[SpaLiveLabels.CurrentTemp] as number ?? 0}°F</span>
              </div>
            </td>
            <td> </td>
            <td> </td>
            <td> </td>

            {/* SETTINGS */}
            <td> 
              <div className="labelled-cell">
              <span>Set Point: </span>
              {editMode ?
                <span className="active">
                  <input id="setTSP" type="number" defaultValue={settingsData.TSP} min={constData[SpaConstLabels.setTSPmin]} max={constData[SpaConstLabels.setTSPmax]} 
                    onChange={(e)=>setNewSettings({...newSettings, setTSP: Number(e.target.value)})}/>
                </span>
                :
                <span className="active">{settingsData.TSP}°F</span>
              }
              </div>
            </td>

            <td>{statusData.STAT61!==null ? statusData.STAT61 ? 'NA': 'Euro' : ''}</td>

            <td>
              {show(settingsData[SpaSettingsLabels.cfgCTMode]) && <div className="labelled-cell">
              <span>CTMode: </span>
              {editMode ? 
              <select name="CTMode" id="cfgCTMode" defaultValue={settingsData[SpaSettingsLabels.cfgCTMode] ?? 0} onChange={(e)=>setNewSettings({...newSettings, cfgCTMode: Number(e.target.value)})}>
                {Array.from({length: Number(constData[SpaConstLabels.cfgCTModemax]) - Number(constData[SpaConstLabels.cfgCTModemin]) + 1}, (_, i) => Number(constData[SpaConstLabels.cfgCTModemin]) + i).map((val) => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
              :
              <span className="labelled-cell">{Number(settingsData[SpaSettingsLabels.cfgCTMode]?? 0)}</span>            
            }</div>}
            </td>

          </tr>

          {/* HEAT 2 */}
          {show(liveData[SpaLiveLabels.HTemp]) && <tr>
            <td className="title"></td>
            <td className="config"></td>
            {/* LIVE */}
            <td ></td>
            <td>
              <div className="labelled-cell">
                <span>Heater: </span>
                <span className="active">{liveData[SpaLiveLabels.HTemp] as number ?? 0}°F</span>
              </div>
            </td>
            <td> </td>
            <td> </td>
            <td> </td>

            {/* SETTINGS */}
            <td> </td>
            <td> </td>
            <td> </td>

          </tr>}

          {/* FILTER */}
          <tr className="striped-row">
            <td className="title"></td>
            <td className="config">Filter</td>

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Filter] ? `active`:``}>{upperCase(filterStateLabels[liveData[SpaLiveLabels.Filter] as unknown as number] ?? '')}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td>
              <div className="labelled-cell">
              <span>FF: </span>
              {editMode ?
              <span className={settingsData.FF ? `active`:``}>
                <input type="number" defaultValue={settingsData.FF} min={constData[SpaConstLabels.setFFmin]} max={constData[SpaConstLabels.setFFmax]} 
                  onChange={(e)=>setNewSettings({...newSettings, setFF: Number(e.target.value)})}/>
              </span>
              :
              <span className={settingsData.FF ? `active`:``}>{settingsData.FF ?? 0}</span>
            }</div>
            </td>

            <td>
              <div className="labelled-cell">
              <span>FD: </span>
              {editMode ?
                <span className={settingsData.FD ? `active`:``}><input type="number" defaultValue={settingsData.FD} min={constData[SpaConstLabels.setFDmin]} max={constData[SpaConstLabels.setFDmax]}
                  onChange={(e)=>setNewSettings({...newSettings, setFD: Number(e.target.value)})}
                /></span>
                :
                <span className={settingsData.FD ? `active`:``}>{settingsData.FD ?? 0}</span>
              }</div>
            </td>

            <td>    
              <div className="labelled-cell">         
              <span>FS: </span>
                {editMode ?
                  <span><Switch size="small" defaultChecked={settingsData.FS} onChange={(e)=>setNewSettings({...newSettings, setFS: e.target.checked})}/></span>
                  :
                  <span className={settingsData.FS ? `active`:``}>{getConfigDisplay(settingsData.FS, `isOn`)}</span>
                }</div> 
            </td>
          </tr>

          {/* PURGE TIME (PT: 15-minute units from midnight, 96 = off) */}
          {canSetPurgeTime && show(settingsData.PT) && <tr className="striped-row">
            <td className="title"></td>
            <td className="config"></td>

            {/* LIVE */}
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td>
              <div className="labelled-cell">
              <span>PT: </span>
              {editMode ? (
                <div className="time-picker-select">
                  <Switch
                    checked={isPTEnabled}
                    onChange={(e) => {
                      setIsPTEnabled(e.target.checked);
                      if (e.target.checked) {
                        // Switch to time picker - use last selected time or current PT if valid
                        const timeToUse = settingsData.PT !== 96 ? settingsData.PT : lastSelectedPT;
                        setCurrentPT(timeToUse);
                        setNewSettings({...newSettings, setPT: timeToUse});
                      } else {
                        // Switch to OFF (PT = 96) - but remember current time first
                        if (settingsData.PT !== 96) {
                          setLastSelectedPT(settingsData.PT);
                        }
                        setCurrentPT(96);
                        setNewSettings({...newSettings, setPT: 96});
                      }
                    }}
                    size="small"
                  />
                  {isPTEnabled && (
                    <div className="time-picker-select">
                      <select
                        value={ptToHoursMinutes(currentPT).hours}
                        onChange={(e) => {
                          const newHours = Number(e.target.value);
                          const currentMinutes = ptToHoursMinutes(currentPT).minutes;
                          const ptValue = hoursMinutesToPt(newHours, currentMinutes);
                          setCurrentPT(ptValue); // Update current PT immediately
                          setLastSelectedPT(ptValue); // Remember this selection
                          setNewSettings({...newSettings, setPT: ptValue});
                        }}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <span>:</span>
                      <select
                        value={ptToHoursMinutes(currentPT).minutes}
                        onChange={(e) => {
                          const newMinutes = Number(e.target.value);
                          const currentHours = ptToHoursMinutes(currentPT).hours;
                          const ptValue = hoursMinutesToPt(currentHours, newMinutes);
                          setCurrentPT(ptValue); // Update current PT immediately
                          setLastSelectedPT(ptValue); // Remember this selection
                          setNewSettings({...newSettings, setPT: ptValue});
                        }}
                      >
                        <option value={0}>00</option>
                        <option value={15}>15</option>
                        <option value={30}>30</option>
                        <option value={45}>45</option>
                      </select>
                    </div>
                  )}
                </div>
              ) : (
                <span className={settingsData.PT ? settingsData.PT!==96 ?`active`:`` : ``}>{settingsData.PT ? formatPTDisplay(settingsData.PT) : 'N/A'}</span>
              )}
              </div>
            </td>
            <td></td>
            <td></td>
          </tr>}

          <tr>
            <td className="title">Pump1</td>
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgP1], `config`)}</td>

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Pump1] ? `active`:``}>{valueMapper[liveData[SpaLiveLabels.Pump1]] ?? valueMapper[0]}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr className="striped-row">
            <td className="title">Pump2</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgP2]} onChange={(e)=>setNewSettings({...newSettings, cfgP2: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgP2], `config`)}</td>
            }
            
            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Pump2] ? `active`:``}>{valueMapper[liveData[SpaLiveLabels.Pump2]] ?? valueMapper[0]}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            <td className="title">Pump3</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgP3]} onChange={(e)=>setNewSettings({...newSettings, cfgP3: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgP3], `config`)}</td>
            }

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Pump3] ? `active`:``}>{valueMapper[liveData[SpaLiveLabels.Pump3]] ?? valueMapper[0]}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr className="striped-row">
            <td className="title">SDS</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgSDS]} onChange={(e)=>setNewSettings({...newSettings, cfgSDS: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgSDS], `config`)}</td>
            }

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.SDS] ? `active`:``}>{valueMapper[liveData[SpaLiveLabels.SDS]] ?? valueMapper[0]}</td>
            <td> </td>
            <td> </td>
            <td> </td>
            <td> </td>

            {/* SETTINGS */}
            <td> </td>
            <td> </td>
            <td> </td>
          </tr>

          <tr>
            <td className="title">YESS</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgYESS]} onChange={(e)=>setNewSettings({...newSettings, cfgYESS: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgYESS], `config`)}</td>
            }

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Yess] ? `active`:``}>{getConfigDisplay(liveData[SpaLiveLabels.Yess],`isOn`)}</td>
            <td> </td>
            <td> </td>
            <td> </td>
            <td> </td>

            {/* SETTINGS */}
            <td> </td>
            <td> </td>
            <td> </td>
          </tr>

          <tr className="striped-row">
            <td className="title">Fogger</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgFG]} onChange={(e)=>setNewSettings({...newSettings, cfgFG: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgFG], `config`)}</td>
            }

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Fogger] ? `active`:``}>{getConfigDisplay(liveData[SpaLiveLabels.Fogger],`isOn`)}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            <td className="title">Ozone Peak1</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgOzP1]} onChange={(e)=>setNewSettings({...newSettings, cfgOzP1: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgOzP1], `config`)}</td>
            }

            {/* LIVE */}
            <td> </td>
            <td className={liveData[SpaLiveLabels.Oz] ? `active`:``}>{getConfigDisplay(liveData[SpaLiveLabels.Oz],`isOn`)}</td>
            <td> </td>
            <td> </td>
            <td> </td>

            {/* SETTINGS */}
            {editMode ?
              <td className={settingsData.OzCy!==0 ? `active`:``}>
                <select name="Ozone Cycle" id="OzCy" defaultValue={settingsData.OzCy} onChange={(e)=>setNewSettings({...newSettings, setOzCy: Number(e.target.value)})}>
                  <option value={0}>OFF</option>
                  <option value={1}>P1low</option>
                  <option value={2}>P1high</option>
                  <option value={3}>Yess</option>
                </select>
              </td>
              :
              <td className={settingsData.OzCy!==0 ? `active`:``}>{ valueMapper[settingsData.OzCy] ?? valueMapper[0]}</td>
            }
            <td> </td>
            <td> </td>
          </tr>

          <tr className="striped-row">
            <td className="title">Ozone Peak2</td>
            {editMode?
            <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgOzP2]} onChange={(e)=>setNewSettings({...newSettings, cfgOzP2: e.target.checked})}></input></td>
            :
            <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgOzP2], `config`)}</td>
            }

            {/* LIVE */}
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>

            {/* SETTINGS */}
            {editMode?
              <td className={settingsData.OzHr ? `active`:``}>
                <input 
                  type="number" 
                  defaultValue={settingsData.OzHr} 
                  min={constData[SpaConstLabels.setOzHrmin]}
                  max={constData[SpaConstLabels.setOzHrmax]}
                  onChange={(e)=>setNewSettings({...newSettings, setOzHr: Number(e.target.value)})}>
                </input> hrs/day
              </td>
              :
              <td className={settingsData.OzHr ? `active`:``}>{settingsData.OzHr ?? 0} hrs/day</td>
            }
            <td > </td>
            <td > </td>
          </tr>

          <tr>
            <td className="title">{palName}</td>
            {editMode?
              <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgSB]} onChange={(e)=>setNewSettings({...newSettings, cfgSB: e.target.checked})}></input></td>
              :
              <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgSB], `config`)}</td>
            }

            {/* LIVE */}
            <td>
                {show(liveData.sbStat) && <span className={liveData.sbStat ? `active`:``}>{`${getConfigDisplay(liveData.sbStat,`isOn`)} `}</span>}
                
                {show(settingsData.SBConnected) && <>(<span className={settingsData.SBConnected ? `active`:``}>{getConfigDisplay(settingsData.SBConnected,`isConnected`)}</span>)</>}

            </td>

            <td>
              {show(liveData.sbpH) && <div className="labelled-cell">
              <span>pH: </span>
              <span className={liveData.sbpH ? `active`:``}>{getConvertedValue(liveData.sbpH)}</span>
              </div>}
            </td>

            <td>
              {show(liveData.sbORP) && <div className="labelled-cell">
              <span>ORP: </span>
              <span className={liveData.sbORP ? `active`:``}>{liveData.sbORP??0} mV</span>
              </div>}
            </td>

            <td>
              {show(liveData.sbWearE1) && <div className="labelled-cell">
              <span>{`E1 Wear: `}</span>
              <span className={liveData.sbWearE1 ? `active`:``}>{liveData.sbWearE1? liveData.sbWearE1===101?'N/A':liveData.sbWearE1:''}</span>
            </div>}</td>

            <td>
              {show(liveData.sbWearE2) && <div className="labelled-cell">
              <span>{`E2 Wear: `}</span>
              <span className={liveData.sbWearE2 ? `active`:``}>{liveData.sbWearE2? liveData.sbWearE2===101?'N/A':liveData.sbWearE2:''}</span>
            </div>}</td>

            {/* SETTINGS */}
            {editMode ?
              <td >
                <span>ORP: </span>
                <select name="CL Level" id="SBORPhi" defaultValue={orpLevelMapperReverse[settingsData.SBORPhi]} onChange={(e)=>setNewSettings({...newSettings, SBORPlo: orpLevelMapper[Number(e.target.value)][0], SBORPhi: orpLevelMapper[Number(e.target.value)][1]})}>
                  <option value={0}>Low</option>
                  <option value={50}>Med</option>
                  <option value={100}>High</option>
                </select>
              </td>
              :
              <td>
                {show(settingsData.SBORPhi) && <div className="labelled-cell">
                <span>ORP Hi: </span>
                <span className={settingsData.SBORPhi ? `active`:``}>{settingsData.SBORPhi??0} mV</span></div>}
              </td>
            } 
            <td>
              {show(settingsData.SBpHhi) && <div className="labelled-cell">
              <span>pH Hi: </span>
              <span className={settingsData.SBpHhi ? `active`:``}>{getConvertedValue(settingsData.SBpHhi)}</span></div>}
            </td>

            {editMode && canEnableManualMode ?
              <td className={settingsData.SBHr ? `active`:``}>
                <input 
                  type="number" 
                  defaultValue={settingsData.SBHr} 
                  min={constData[SpaConstLabels.setSBHrsmin]??0}
                  max={constData[SpaConstLabels.setSBHrsmax]??24}
                  onChange={(e)=>setNewSettings({...newSettings, setSBHrs: Number(e.target.value)})}>
                </input> hrs/day
              </td>
              :
              <td className={settingsData.SBHr ? `active`:``}>{show(settingsData.SBHr) && `${settingsData.SBHr ?? 0} hrs/day`}</td>
            }
          </tr>

          {/* Also carries the Spa Boy electrode readings, so it stays when a V1 spa has those */}
          {show(settingsData[SpaSettingsLabels.cfgSP], liveData.sbVout, liveData.sbI1, liveData.sbI2, settingsData.SBORPlo, settingsData.SBpHlo) && <tr>
            <td className="title">{show(settingsData[SpaSettingsLabels.cfgSP]) && 'Smart pH'}</td>
            {editMode?
              <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgSP]} onChange={(e)=>setNewSettings({...newSettings, cfgSP: e.target.checked})}></input></td>
              :
              <td className="config">{show(settingsData[SpaSettingsLabels.cfgSP]) && getConfigDisplay(settingsData[SpaSettingsLabels.cfgSP], `config`)}</td>
            }

            {/* LIVE */}            
            <td className={liveData[SpaLiveLabels.phSM]??0 > 0 ? `active`:``} style={{textTransform: 'uppercase'}}>{show(liveData[SpaLiveLabels.phSM]) && SmartPHStateTextMap[(liveData[SpaLiveLabels.phSM] ?? 0) as SmartPHState]}</td>

            {/* Boost button - if spa has smart ph */}
            <td>
              
            </td>

            <td>
              {show(liveData.sbVout) && <div className="labelled-cell">
              <span>Evolts: </span>
              <span className={liveData.sbVout ? `active`:``}>{liveData.sbVout} mV</span>
              </div>}
            </td>

            <td>
              {show(liveData.sbI1) && <div className="labelled-cell">
              <span>{`E1 Amps: `}</span>
              <span className={liveData.sbI1 ? `active`:``}>{liveData.sbI1} mA</span></div>}
            </td>

            <td>
              {show(liveData.sbI2) && <div className="labelled-cell">
              <span>{`E2 Amps: `}</span>
              <span className={liveData.sbI2 ? `active`:``}>{liveData.sbI2} mA</span></div>}
            </td>

            {/* SETTINGS */}
            {editMode ?
              <td>
              </td>
              :
              <td>
                {show(settingsData.SBORPlo) && <div className="labelled-cell">
                <span>ORP Lo: </span>
                <span className={settingsData.SBORPlo ? `active`:``}>{settingsData.SBORPlo??0} mV</span></div>}
              </td>
            } 
            <td>
              {show(settingsData.SBpHlo) && <div className="labelled-cell">
              <span>pH Lo: </span>
              <span className={settingsData.SBpHlo ? `active`:``}>{getConvertedValue(settingsData.SBpHlo)}</span></div>}
            </td>
            <td ></td>
          </tr>}

          <tr className="striped-row">
            <td className="title">Onzen</td>
            {editMode?
              <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgOn]} onChange={(e)=>setNewSettings({...newSettings, cfgOn: e.target.checked})}></input></td>
              :
              <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgOn], `config`)}</td>
            }

            {/* LIVE */}
            <td  className={liveData.On ? `active`:``}>{getConfigDisplay(liveData.On,`isOn`)}</td>
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>

            {/* SETTINGS */}
            {editMode?
              <td className={settingsData.OnHr ? `active`:``}>
                <input 
                  type="number" 
                  defaultValue={settingsData.OnHr} 
                  min={constData[SpaConstLabels.setOnHrmin]}
                  max={constData[SpaConstLabels.setOnHrmax]}
                  onChange={(e)=>setNewSettings({...newSettings, setOnHr: Number(e.target.value)})}>
                </input> hrs/day
              </td>
              :
              <td className={settingsData.OnHr ? `active`:``}>{settingsData.OnHr ?? 0} hrs/day</td>
            }
            <td > </td>
            <td > </td>
          </tr>

          {show(settingsData[SpaSettingsLabels.cfgRFID]) && <tr>
            <td className="title">RFID</td>
            {editMode?
              <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgRFID]} onChange={(e)=>setNewSettings({...newSettings, cfgRFID: e.target.checked})}></input></td>
              :
              <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgRFID], `config`)}</td>
            }
            
            {/* LIVE */}
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>}

          <tr className="striped-row">
            <td className="title">Exhaust</td>
            {editMode ?
              <td className="config"><input type="checkbox" defaultChecked={settingsData[SpaSettingsLabels.cfgEx]} onChange={(e)=>setNewSettings({...newSettings, cfgEx: e.target.checked})}></input></td>
              :
              <td className="config">{getConfigDisplay(settingsData[SpaSettingsLabels.cfgEx], `config`)}</td>
            }
            {/* LIVE */}
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            <td className="title">Lights</td>
            {editMode ?
              <td className="config">
                <select name="Lights" id="cfgLi" defaultValue={settingsData[SpaSettingsLabels.cfgLi] ? 1 : 0} onChange={(e)=>setNewSettings({...newSettings, cfgLi: e.target.value === '1'})}>
                  <option value={0}>RGB</option>
                  <option value={1}>Standard</option>
                </select>
              </td>
              :
              <td className="config">{settingsData[SpaSettingsLabels.cfgLi] ? 'Standard' : 'RGB'}</td>
            }

            {/* LIVE */}
            <td className={liveData[SpaLiveLabels.Lights] ? `active`:``}>{getConfigDisplay(liveData[SpaLiveLabels.Lights],`isOn`)}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>

            {/* SETTINGS */}
            <td></td>
            <td></td>
            <td></td>
          </tr>

          <tr className="striped-row">
          <td className="title">Breaker Count</td>
            {editMode ? 
              <td className="config">
                <select name="Breaker Count" id="cfgPL" defaultValue={1} onChange={(e)=>setNewSettings({...newSettings, cfgPL: Number(e.target.value)})}>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </td>
              :
              <td className="config">{settingsData[SpaSettingsLabels.cfgPL] ?? 0}</td>            
            }
            
            {/* LIVE */}
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>

            {/* SETTINGS */}
            <td > </td>
            <td > </td>
            <td > </td>
          </tr>

          <tr>
            <td className="title">Breaker Size</td>
            {editMode ? 
              <td className="config">
                <select name="Breaker Size" id="cfgBS" defaultValue={settingsData[SpaSettingsLabels.cfgBS] ?? 60} onChange={(e)=>setNewSettings({...newSettings, cfgBS: Number(e.target.value)})}>
                  <option value={16}>16</option>
                  <option value={20}>20</option>
                  <option value={25}>25</option>
                  <option value={32}>32</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                  <option value={60}>60</option>
                </select>
              </td>
              :
              <td className="config">{settingsData[SpaSettingsLabels.cfgBS] ?? 0}</td>            
            }

            {/* LIVE */}
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>

            {/* SETTINGS */}
            <td > </td>
            <td > </td>
            <td > </td>
          </tr>

          <tr className="striped-row">
            <td className="title">Econ Mode</td>
            <td className="config"> </td>

            {/* LIVE */}
            <td className={liveData.Econ ? `active`:``}>{getConfigDisplay(liveData.Econ,`isOn`)}</td>
            <td > </td>
            <td > </td>
            <td > </td>
            <td > </td>

            {/* SETTINGS */}
            <td>
              <button
                className="sd-grid-btn"
                onClick={() => navigate(`/spa-control/${spa?.id}/peak-management`)}
                aria-label="Open econ mode settings"
              >
                Econ settings <ChevronRightIcon/>
              </button>
            </td>
            <td > </td>
            <td > </td>
          </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DetailsGrid;
